
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { formatPrice } from "@/utils/number";
import ConfigContainerPackingDetailDrawer from "@/views/sales-management/components/ConfigContainerPackingDetailDrawer.vue";
import { CustomsPackingItem } from "@/views/sales-management/components/configContainerTypeModels";
import {
  isOverUnderageBlocked,
  packingOverUnderageMarks,
  PackingListGroup,
  PackingRow,
} from "./henganLoadingPlanFormModels";

@Component({
  name: "HenganLoadingPlanPackingBlock",
  components: { ConfigContainerPackingDetailDrawer },
})
export default class HenganLoadingPlanPackingBlock extends Vue {
  @Prop({ required: true }) private group!: PackingListGroup;
  @Prop({ required: true }) private mode!: "unadded" | "added";
  @Prop({ required: false, default: true }) private expanded!: boolean;
  @Prop({ required: false, default: 0 }) private selectionTick!: number;

  private headerChecked = false;
  private detailVisible = false;
  private activePacking: CustomsPackingItem | null = null;

  @Watch("selectionTick")
  private onSelectionTickChange() {
    this.syncHeaderCheckbox();
  }

  @Watch("group", { deep: true, immediate: true })
  private onGroupChange() {
    this.syncHeaderCheckbox();
  }

  private get selectableItems(): PackingRow[] {
    return (this.group.items || []).filter((row) => this.canSelect(row));
  }

  private get groupVolume(): string {
    return (this.group.items || [])
      .reduce((sum, row) => sum + Number(row.volume || 0), 0)
      .toFixed(2);
  }

  private get groupPrice(): string {
    const total = (this.group.items || []).reduce(
      (sum, row) => sum + Number(row.totalPrice || 0),
      0
    );
    return formatPrice(total);
  }

  private get allUnboxing(): boolean {
    const items = this.group.items || [];
    return items.length > 0 && items.every((row) => !!row.unboxing);
  }

  private get someUnboxing(): boolean {
    return (this.group.items || []).some((row) => !!row.unboxing);
  }

  private get allLoadingFirst(): boolean {
    const items = this.group.items || [];
    return items.length > 0 && items.every((row) => !!row.loadingFirst);
  }

  private get someLoadingFirst(): boolean {
    return (this.group.items || []).some((row) => !!row.loadingFirst);
  }

  private rowKey(row: PackingRow) {
    return row.packingId || row.id;
  }

  private displayCode(row: PackingRow) {
    return row.packingCode || row.displayCode || "-";
  }

  private displayWeight(value: string | number | null | undefined) {
    if (value === null || value === undefined || value === "" || value === "null") {
      return "--";
    }
    return value;
  }

  private isStock(row: PackingRow) {
    return row.inventory === "YES" || row.inventory === "DEPLOY";
  }

  private packingMarks(row: PackingRow) {
    return packingOverUnderageMarks(row.overUnderageStatus);
  }

  private openDetail(row: PackingRow) {
    // 已添加：packingId 才是主包装 id；id 是出库计划关联记录 id
    const packingId = row.packingId || row.id;
    if (!packingId) {
      return;
    }
    const packing = new CustomsPackingItem();
    packing.id = packingId;
    packing.code = this.displayCode(row);
    packing.overUnderageStatus = row.overUnderageStatus as any;
    packing.grossWeight = String(row.grossWeight ?? "");
    packing.netWeight = String(row.netWeight ?? "");
    packing.volume = String(row.volume ?? "");
    this.activePacking = packing;
    this.detailVisible = true;
  }

  private canSelect(row: PackingRow) {
    if (this.mode === "added") {
      return true;
    }
    return !(
      row.inventory === "MIX" ||
      row.inventory === "YES" ||
      isOverUnderageBlocked(row.overUnderageStatus) ||
      row.exception
    );
  }

  private rowClassName({ row }: { row: PackingRow }) {
    if (this.mode === "added" && row.loadingFirst) {
      return "success-row";
    }
    return "";
  }

  private toggleExpanded() {
    this.$emit("update:expanded", !this.expanded);
  }

  private onToggleAll(checked: boolean) {
    this.selectableItems.forEach((row) => {
      this.$set(row, "selected", checked);
    });
    this.syncHeaderCheckbox();
    this.$emit("selection-change");
  }

  private onToggleRow(row: PackingRow, checked: boolean) {
    this.$set(row, "selected", checked);
    this.syncHeaderCheckbox();
    this.$emit("selection-change");
  }

  private toggleAllUnboxing(checked: boolean) {
    (this.group.items || []).forEach((row) => {
      this.$set(row, "unboxing", checked);
    });
  }

  private toggleAllLoadingFirst(checked: boolean) {
    (this.group.items || []).forEach((row) => {
      this.$set(row, "loadingFirst", checked);
    });
  }

  private syncHeaderCheckbox() {
    const selectable = this.selectableItems;
    const selectedCount = selectable.filter((row) => row.selected).length;
    this.headerChecked = selectable.length > 0 && selectedCount === selectable.length;
    this.$nextTick(() => {
      const el = this.$refs.headerCheckbox as HTMLInputElement | undefined;
      if (el) {
        el.indeterminate =
          selectedCount > 0 && selectedCount < selectable.length;
      }
    });
  }
}
